import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Loader2, Lock, CheckCircle2, AlertCircle, ArrowLeft, Tag, Truck, XCircle } from "lucide-react";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";
import { useCart } from "@/lib/cart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUPON_KEY = "tcsw_coupon_v1";

/**
 * Checkout — buyer info + shipping method + Square Web Payments SDK card widget.
 * Flow:
 *   1. On mount, fetch /api/checkout/config to get Square appId + locationId + environment.
 *   2. Debounced re-quote via POST /api/checkout/quote whenever cart / shipping-address /
 *      shipping method / coupon change. We NEVER trust the client's own total.
 *   3. Dynamically load Square Web Payments SDK (sandbox or prod based on env).
 *   4. On submit: tokenize card via SDK → POST source_id + idempotency_key + cart + address
 *      + shipping_method + coupon_code.
 *   5. Redirect to /checkout/success?order=... on success.
 */
export default function Checkout() {
  const { items, clear } = useCart();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [loadingSdk, setLoadingSdk] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COUPON_KEY) || "null")?.code || ""; } catch { return ""; }
  });
  const [couponInput, setCouponInput] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("");
  const [buyer, setBuyer] = useState({
    email: "",
    name: "",
    phone: "",
    street1: "",
    street2: "",
    city: "",
    state: "ON",
    postal_code: "",
    country: "CA",
  });
  const cardRef = useRef(null);
  const paymentsRef = useRef(null);
  const quoteTimer = useRef(null);

  useSEO({
    title: "Checkout",
    description: "Complete your custom print order — secure payment powered by Square.",
    path: "/checkout",
  });

  // Redirect if cart is empty
  useEffect(() => {
    if (items.length === 0 && !processing) navigate("/cart", { replace: true });
  }, [items.length, processing, navigate]);

  // Load Square config
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/checkout/config`).then(({ data }) => {
      if (!cancelled) setConfig(data);
    }).catch(() => {
      if (!cancelled) toast.error("Payments are temporarily unavailable. Please try again later.");
    });
    return () => { cancelled = true; };
  }, []);

  const lines = useMemo(() => items.map((it) => ({
    product_id: it.product_id,
    option_ids: it.option_ids,
    quantity: it.quantity,
    // Pre-uploaded artwork token flows through so the server auto-attaches
    // it to the created order (skips the post-checkout email upload step).
    artwork_token: it.artwork_token || undefined,
    artwork_filename: it.artwork_filename || undefined,
    artwork_size: it.artwork_size || undefined,
  })), [items]);

  const artworkLinesCount = useMemo(() => items.filter((it) => it.artwork_token).length, [items]);

  // Server-side re-quote, debounced. Includes shipping_address once buyer fills postal code.
  const hasShippingAddress = Boolean(buyer.city && buyer.state && buyer.postal_code && buyer.street1);

  useEffect(() => {
    if (items.length === 0) return;
    if (quoteTimer.current) clearTimeout(quoteTimer.current);
    setQuoteError(null);
    setQuoting(true);
    const body = { lines };
    if (hasShippingAddress) {
      body.shipping_address = {
        name: buyer.name || "Customer",
        street1: buyer.street1,
        street2: buyer.street2,
        city: buyer.city,
        state: buyer.state,
        postal_code: buyer.postal_code,
        country: buyer.country || "CA",
        phone: buyer.phone,
      };
    }
    if (shippingMethod) body.shipping_method = shippingMethod;
    if (couponCode) body.coupon_code = couponCode;

    quoteTimer.current = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/checkout/quote`, body);
        setQuote(data);
        // If server rejected the coupon (invalid flag), drop it silently.
        if (data.coupon?.invalid) {
          setCouponCode("");
          localStorage.removeItem(COUPON_KEY);
          toast.error("Your saved coupon is no longer valid.");
        }
        // If no method chosen yet, auto-select the cheapest quote.
        if (data.shipping_quotes?.length && !shippingMethod) {
          setShippingMethod(data.shipping_quotes[0].method);
        }
      } catch (err) {
        setQuote(null);
        setQuoteError(err.response?.data?.detail || "Unable to price your cart. Please try again.");
      } finally {
        setQuoting(false);
      }
    }, 350);
    return () => { if (quoteTimer.current) clearTimeout(quoteTimer.current); };
  }, [lines, hasShippingAddress, buyer.name, buyer.street1, buyer.street2, buyer.city, buyer.state, buyer.postal_code, buyer.country, buyer.phone, shippingMethod, couponCode, items.length]);

  // Load Square Web Payments SDK once config is known.
  // KEY: We pass `postalCode` at attach time so Square's card element does NOT
  // render its own postal input. This is necessary because our Square sandbox
  // merchant location is US-based, so the widget would otherwise validate as a
  // 5-digit US ZIP and reject Canadian alpha postals ("L3R 1G3").
  // We supply a valid US sandbox ZIP for the tokenization AVS handshake — the
  // buyer's real Canadian postal is captured separately in buyer.postal_code
  // and sent to the backend as the shipping address.
  const SQUARE_TOKEN_POSTAL = "94103";
  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    const attach = async () => {
      try {
        const src = config.environment === "sandbox"
          ? "https://sandbox.web.squarecdn.com/v1/square.js"
          : "https://web.squarecdn.com/v1/square.js";
        if (!document.querySelector(`script[src="${src}"]`)) {
          await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = src;
            s.onload = resolve;
            s.onerror = () => reject(new Error("Failed to load Square SDK"));
            document.head.appendChild(s);
          });
        }
        if (cancelled || !window.Square) return;
        const payments = window.Square.payments(config.application_id, config.location_id);
        paymentsRef.current = payments;
        const card = await payments.card({ postalCode: SQUARE_TOKEN_POSTAL });
        await card.attach("#square-card-container");
        if (!cancelled) {
          cardRef.current = card;
          setLoadingSdk(false);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          toast.error("Could not initialise the payment form.");
        }
      }
    };
    attach();
    return () => {
      cancelled = true;
      if (cardRef.current) {
        try { cardRef.current.destroy(); } catch { /* noop */ }
        cardRef.current = null;
      }
    };
  }, [config]);

  const canSubmit = useMemo(() => {
    if (!quote || processing || quoting) return false;
    if (!buyer.email || !buyer.name || !buyer.street1 || !buyer.city || !buyer.state || !buyer.postal_code) return false;
    // Require shipping selection when quotes are available
    if ((quote.shipping_quotes?.length || 0) > 0 && !shippingMethod) return false;
    return true;
  }, [quote, processing, quoting, buyer, shippingMethod]);

  const handleField = useCallback((k) => (e) => {
    setBuyer((b) => ({ ...b, [k]: e.target.value }));
  }, []);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setApplyingCoupon(true);
    try {
      const { data } = await axios.post(`${API}/checkout/coupons/validate`, { code });
      if (data.valid) {
        setCouponCode(data.code);
        localStorage.setItem(COUPON_KEY, JSON.stringify({ code: data.code, kind: data.kind, value: data.value }));
        setCouponInput("");
        toast.success(`Coupon "${data.code}" applied.`);
      } else {
        toast.error("That coupon isn't valid or has expired.");
      }
    } catch {
      toast.error("Could not validate coupon. Try again.");
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode("");
    localStorage.removeItem(COUPON_KEY);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || !cardRef.current) return;
    setProcessing(true);
    try {
      const result = await cardRef.current.tokenize();
      if (result.status !== "OK") {
        const msg = result.errors?.[0]?.message || "Card details are invalid.";
        toast.error(msg);
        setProcessing(false);
        return;
      }
      const idempotencyKey = (crypto.randomUUID && crypto.randomUUID()) || `tcsw-${Date.now()}-${Math.random()}`;
      const payload = {
        source_id: result.token,
        idempotency_key: idempotencyKey,
        email: buyer.email.trim(),
        note: `Buyer: ${buyer.name.trim()}`,
        lines,
        shipping: {
          name: buyer.name.trim(),
          street1: buyer.street1.trim(),
          street2: buyer.street2.trim(),
          city: buyer.city.trim(),
          state: buyer.state.trim(),
          postal_code: buyer.postal_code.trim(),
          country: buyer.country || "CA",
          phone: buyer.phone.trim(),
        },
        shipping_method: shippingMethod || null,
        coupon_code: couponCode || null,
      };
      const { data } = await axios.post(`${API}/checkout/create-payment`, payload);
      try { sessionStorage.setItem("tcsw_last_order", JSON.stringify(data)); } catch { /* noop */ }
      localStorage.removeItem(COUPON_KEY);
      clear();
      navigate(`/checkout/success?order=${encodeURIComponent(data.order_id)}`, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail?.error)
        ? detail.error.map((e) => e.detail || e.code).join(", ")
        : (detail?.error || detail || err.message || "Payment failed. Please try again.");
      toast.error(typeof msg === "string" ? msg : "Payment failed. Please try again.");
      setProcessing(false);
    }
  };

  const shippingQuotes = quote?.shipping_quotes || [];

  return (
    <div className="pt-28 pb-24 bg-ink-950 min-h-screen" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <nav className="text-[10px] uppercase tracking-[0.24em] text-white/40 mb-4 flex items-center gap-2">
          <Link to="/shop" className="hover:text-gold">Print Shop</Link>
          <ChevronRight size={11} />
          <Link to="/cart" className="hover:text-gold">Cart</Link>
          <ChevronRight size={11} />
          <span className="text-gold">Checkout</span>
        </nav>

        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="eyebrow gold-bar">Secure checkout</div>
            <h1 className="font-display text-4xl md:text-5xl mt-3 tracking-tight" data-testid="checkout-title">Complete your order</h1>
          </div>
          <Link to="/cart" data-testid="back-to-cart" className="text-[11px] uppercase tracking-[0.22em] text-white/50 hover:text-gold flex items-center gap-2">
            <ArrowLeft size={12} /> Back to cart
          </Link>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,400px)] gap-8">
          {/* Left column: buyer + shipping address + shipping method + card */}
          <div className="space-y-8">
            <Section title="Contact">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Email" value={buyer.email} onChange={handleField("email")} type="email" required testId="checkout-email" />
                <Input label="Full name" value={buyer.name} onChange={handleField("name")} required testId="checkout-name" />
                <Input label="Phone" value={buyer.phone} onChange={handleField("phone")} type="tel" testId="checkout-phone" wrap />
              </div>
            </Section>

            <Section title="Shipping address">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Street address" value={buyer.street1} onChange={handleField("street1")} required testId="checkout-street1" wrap />
                <Input label="Apartment / suite (optional)" value={buyer.street2} onChange={handleField("street2")} testId="checkout-street2" wrap />
                <Input label="City" value={buyer.city} onChange={handleField("city")} required testId="checkout-city" />
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Province</span>
                  <select
                    data-testid="checkout-state"
                    value={buyer.state}
                    onChange={handleField("state")}
                    className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
                  >
                    {["ON","QC","BC","AB","MB","SK","NS","NB","NL","PE","YT","NT","NU"].map((p) => <option key={p}>{p}</option>)}
                  </select>
                </label>
                <Input label="Postal code" value={buyer.postal_code} onChange={handleField("postal_code")} required testId="checkout-postal" />
                <label className="block">
                  <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Country</span>
                  <input value="Canada" disabled className="w-full bg-ink-800 border border-white/10 text-white/60 px-4 py-3 text-sm rounded-sm" />
                </label>
              </div>
            </Section>

            <Section title="Shipping method" icon={<Truck size={13} className="text-gold" />}>
              {!hasShippingAddress ? (
                <p className="text-white/50 text-sm">Enter your shipping address to see delivery options.</p>
              ) : quoting && shippingQuotes.length === 0 ? (
                <p className="text-white/50 text-sm flex items-center gap-2">
                  <Loader2 size={13} className="animate-spin" /> Fetching shipping rates…
                </p>
              ) : shippingQuotes.length === 0 ? (
                <p className="text-white/50 text-sm">Shipping quotes are unavailable right now. You may still place the order — we&apos;ll email you the shipping options.</p>
              ) : (
                <div className="space-y-2" data-testid="shipping-method-list">
                  {shippingQuotes.map((q) => {
                    const active = shippingMethod === q.method;
                    return (
                      <label
                        key={`${q.carrier}-${q.method}`}
                        data-testid={`shipping-option-${q.method.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`flex items-center justify-between gap-4 p-4 border cursor-pointer transition-colors ${active ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/25"}`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            name="shipping_method"
                            value={q.method}
                            checked={active}
                            onChange={() => setShippingMethod(q.method)}
                            className="mt-1 accent-[var(--color-gold,#c8a25a)]"
                          />
                          <div>
                            <div className="text-white text-sm">{q.method}</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">
                              {q.carrier} · {q.days} business day{q.days === 1 ? "" : "s"}
                            </div>
                          </div>
                        </div>
                        <div className="text-gold font-display text-lg whitespace-nowrap">${(q.price_cents / 100).toFixed(2)}</div>
                      </label>
                    );
                  })}
                </div>
              )}
            </Section>

            <Section title="Payment" icon={<Lock size={13} className="text-gold" />}>
              <div className="text-[11px] text-white/50 mb-4 flex items-center gap-2">
                <Lock size={11} className="text-gold" />
                Card details are encrypted by Square. We never see or store your card number.
              </div>
              <div className="relative min-h-[64px]">
                {loadingSdk && (
                  <div className="absolute inset-0 flex items-center gap-2 text-white/60 text-sm">
                    <Loader2 size={14} className="animate-spin" /> Loading secure payment form…
                  </div>
                )}
                <div
                  id="square-card-container"
                  data-testid="square-card-container"
                  className={loadingSdk ? "opacity-0" : "opacity-100 transition-opacity duration-300"}
                />
              </div>
              {config?.environment === "sandbox" && (
                <div className="mt-4 border border-purple-hi/30 bg-purple/10 p-3 rounded-sm text-[11px] text-white/70">
                  <span className="text-purple-hi font-semibold uppercase tracking-wider text-[10px]">Sandbox mode</span>
                  <span className="ml-2">Test card: <code className="bg-black/40 px-1.5 py-0.5 rounded">4111 1111 1111 1111</code> · any future exp · any CVV · <span className="text-white/85">leave the pre-filled ZIP as-is</span></span>
                </div>
              )}
            </Section>
          </div>

          {/* Right column: order summary + submit */}
          <aside className="lg:sticky lg:top-24 self-start">
            <div className="card-dark p-7" data-testid="checkout-summary">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Order summary</div>

              {quoteError ? (
                <div className="mt-4 flex items-start gap-3 border border-red-500/30 bg-red-500/10 p-4 rounded-sm text-sm">
                  <AlertCircle size={16} className="text-red-400 shrink-0 mt-0.5" />
                  <div className="text-red-200">{typeof quoteError === "string" ? quoteError : "Cart could not be re-priced."}</div>
                </div>
              ) : quote ? (
                <>
                  <ul className="mt-5 space-y-3 border-b border-white/5 pb-4">
                    {quote.lines.map((l, i) => (
                      <li key={i} className="flex items-start justify-between gap-3 text-sm" data-testid={`checkout-line-${l.product_id}`}>
                        <div className="min-w-0">
                          <div className="text-white truncate" title={l.product_name}>{l.product_name}</div>
                          <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mt-0.5">
                            {Object.values(l.selection || {}).slice(0, 3).join(" · ")} · Qty {l.quantity}
                          </div>
                        </div>
                        <div className="text-white/85 text-sm whitespace-nowrap">${(l.line_total_cents / 100).toFixed(2)}</div>
                      </li>
                    ))}
                  </ul>

                  {/* Coupon row */}
                  <div className="mt-4">
                    {couponCode && quote.coupon && !quote.coupon.invalid ? (
                      <div className="flex items-center justify-between gap-3 border border-gold/40 bg-gold/5 p-2.5 rounded-sm" data-testid="checkout-coupon-applied">
                        <div className="flex items-center gap-2 text-sm">
                          <Tag size={12} className="text-gold" />
                          <div>
                            <div className="text-white/85 font-mono text-[12px]">{quote.coupon.code}</div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-white/50">
                              {quote.coupon.kind === "percent" ? `${quote.coupon.value}% off` : `$${Number(quote.coupon.value).toFixed(2)} off`}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeCoupon}
                          data-testid="checkout-coupon-remove"
                          className="text-white/50 hover:text-red-400 transition-colors"
                          aria-label="Remove coupon"
                        >
                          <XCircle size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                          placeholder="Coupon code"
                          data-testid="checkout-coupon-input"
                          className="flex-1 bg-ink-800 border border-white/10 text-white px-3 py-2 text-sm rounded-sm outline-none focus:border-gold font-mono uppercase"
                        />
                        <button
                          type="button"
                          onClick={applyCoupon}
                          disabled={!couponInput.trim() || applyingCoupon}
                          data-testid="checkout-coupon-apply"
                          className="text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-gold text-gold hover:bg-gold hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {applyingCoupon ? "…" : "Apply"}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 space-y-2 text-sm">
                    <Row label="Subtotal" value={`$${(quote.subtotal_cents / 100).toFixed(2)}`} />
                    {(quote.discount_cents || 0) > 0 && (
                      <Row label={`Discount (${quote.coupon?.code || ""})`} value={`− $${(quote.discount_cents / 100).toFixed(2)}`} gold testId="checkout-discount" />
                    )}
                    <Row
                      label="Shipping"
                      value={quote.shipping_cents > 0 ? `$${(quote.shipping_cents / 100).toFixed(2)}` : (hasShippingAddress ? "Select a method" : "Enter address")}
                      muted={quote.shipping_cents === 0}
                      testId="checkout-shipping"
                    />
                    <Row label={`Tax (${(quote.tax_rate * 100).toFixed(0)}%)`} value={`$${(quote.tax_cents / 100).toFixed(2)}`} />
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/5 flex justify-between items-baseline">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-white/60">Charged today</span>
                    <span className="font-display text-3xl text-gold" data-testid="checkout-total">${(quote.total_cents / 100).toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="mt-4 text-white/50 text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Calculating…
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="submit-payment-btn"
                className="btn-gold w-full justify-center mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {processing ? <><Loader2 size={14} className="animate-spin" /> Processing…</> : <><Lock size={14} /> Pay now</>}
              </button>

              <div className="mt-4 pt-4 border-t border-white/5 text-[10px] uppercase tracking-[0.22em] text-white/40 space-y-2">
                <div className="flex items-center gap-2"><CheckCircle2 size={11} className="text-gold" /> Secure card processing</div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={11} className={artworkLinesCount > 0 ? "text-emerald-400" : "text-gold"} />
                  {artworkLinesCount > 0
                    ? `${artworkLinesCount} artwork file${artworkLinesCount > 1 ? "s" : ""} attached · no email needed`
                    : "Artwork upload link emailed after payment"}
                </div>
                <div className="flex items-center gap-2"><CheckCircle2 size={11} className="text-gold" /> Support at torontocustomt@gmail.com</div>
              </div>
            </div>

            <div className="mt-6 text-[11px] text-white/40 leading-relaxed">
              By placing this order you agree to our terms. {artworkLinesCount > 0
                ? "Your artwork is attached and will ship to production automatically."
                : "You'll receive a confirmation email with an artwork upload link."}
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="card-dark p-7" data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{title}</div>
        {icon}
      </div>
      {children}
    </section>
  );
}

function Input({ label, value, onChange, type = "text", required, testId, wrap }) {
  return (
    <label className={`block ${wrap ? "sm:col-span-2" : ""}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
      />
    </label>
  );
}

function Row({ label, value, muted, gold, testId }) {
  return (
    <div className="flex justify-between" data-testid={testId}>
      <span className={muted ? "text-white/40 text-xs" : gold ? "text-gold" : "text-white/60"}>{label}</span>
      <span className={muted ? "text-white/40 text-xs" : gold ? "text-gold" : "text-white/85"}>{value}</span>
    </div>
  );
}
